import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatDateTime } from 'shared-ui/lib/utils/format-date';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { routesConfig } from '../../config/routes.config';
import {
  useApproveServiceCallApplicationMutation,
  useReactivateServiceCallApplicationMutation,
  useRejectServiceCallApplicationMutation,
  useRevokeServiceCallApplicationMutation,
  useServiceCallApplicationQuery,
} from '../../generated/graphql';
import { useGQLContext } from '../../hooks/use-gql-context';

const statusLabels: Record<string, string> = {
  pending: 'Odottaa',
  approved: 'Hyväksytty',
  rejected: 'Hylätty',
  inactive: 'Ei aktiivinen',
};

export const ServiceCallApplicationShowScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceCallApplicationIndexScene');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const context = useGQLContext();

  const { data, loading } = useServiceCallApplicationQuery({
    variables: { id },
    context,
  });

  const backToIndex = () => navigate(routesConfig.SERVICE_CALL_APPLICATION.INDEX);

  const [approve] = useApproveServiceCallApplicationMutation({
    context,
    onCompleted: (d) => {
      if (d.aApproveServiceCallApplication) {
        toast.success(t('approved'));
        backToIndex();
      }
    },
  });

  const [reject] = useRejectServiceCallApplicationMutation({
    context,
    onCompleted: (d) => {
      if (d.aRejectServiceCallApplication) {
        toast.success(t('rejected'));
        backToIndex();
      }
    },
  });

  const [revoke] = useRevokeServiceCallApplicationMutation({
    context,
    onCompleted: (d) => {
      if (d.aRevokeServiceCallApplication) {
        toast.success(t('accessRevoked'));
        backToIndex();
      }
    },
  });

  const [reactivate] = useReactivateServiceCallApplicationMutation({
    context,
    onCompleted: (d) => {
      if (d.aReactivateServiceCallApplication) {
        toast.success(t('reactivated'));
        backToIndex();
      }
    },
  });

  if (loading) return <div className="p-6">...</div>;

  const application = data?.aServiceCallApplication;

  if (!application) return <div className="p-6">{t('notFound')}</div>;

  const rows: { label: string; value: string | number }[] = [
    { label: t('ServiceCallApplication:officialName'), value: application.officialName },
    { label: t('ServiceCallApplication:businessId'), value: application.businessId },
    { label: t('ServiceCallApplication:streetAddress'), value: application.streetAddress },
    { label: t('ServiceCallApplication:postalCode'), value: application.postalCode },
    { label: t('ServiceCallApplication:city'), value: application.city },
    { label: t('ServiceCallApplication:contactName'), value: application.contactName },
    { label: t('ServiceCallApplication:phone'), value: application.phone },
    { label: t('ServiceCallApplication:email'), value: application.email },
    { label: t('ServiceCallApplication:industry'), value: application.industry },
    {
      label: t('ServiceCallApplication:personnelCount'),
      value: application.personnelCount ?? '-',
    },
    {
      label: t('ServiceCallApplication:status'),
      value: statusLabels[application.status] ?? application.status,
    },
    {
      label: t('ServiceCallApplication:createdAt'),
      value: formatDateTime(application.createdAt),
    },
    {
      label: t('ServiceCallApplication:handledAt'),
      value: application.handledAt ? formatDateTime(application.handledAt) : '-',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg">
        <h2 className="text-lg leading-6 font-bold text-primary mb-4">{application.officialName}</h2>

        <dl className="divide-y divide-gray-200">
          {rows.map((row) => (
            <div key={row.label} className="py-2 grid grid-cols-3 gap-2">
              <dt className="text-sm font-medium text-gray-500">{row.label}</dt>
              <dd className="text-sm text-gray-900 col-span-2">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap gap-2">
          {application.status === 'pending' ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('confirmApprove', { name: application.officialName }))) {
                    approve({ variables: { id: application.id } });
                  }
                }}
                className="py-2 px-4 rounded-md text-sm font-medium text-white bg-primary hover:bg-secondary"
              >
                {t('approve')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('confirmReject', { name: application.officialName }))) {
                    reject({ variables: { id: application.id } });
                  }
                }}
                className="py-2 px-4 rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600"
              >
                {t('reject')}
              </button>
            </>
          ) : null}

          {application.status === 'approved' ? (
            <button
              type="button"
              onClick={() => {
                if (confirm(t('confirmRevokeAccess', { name: application.officialName }))) {
                  revoke({ variables: { id: application.id } });
                }
              }}
              className="py-2 px-4 rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600"
            >
              {t('revokeAccess')}
            </button>
          ) : null}

          {application.status === 'inactive' ? (
            <button
              type="button"
              onClick={() => {
                if (confirm(t('confirmReactivate', { name: application.officialName }))) {
                  reactivate({ variables: { id: application.id } });
                }
              }}
              className="py-2 px-4 rounded-md text-sm font-medium text-white bg-primary hover:bg-secondary"
            >
              {t('reactivate')}
            </button>
          ) : null}

          <Link
            to={routesConfig.SERVICE_CALL_APPLICATION.INDEX}
            className="py-2 px-4 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            {t('Common:back')}
          </Link>
        </div>
      </div>
    </div>
  );
};
